// Tabbed Reviews Carousel Component
// Depends on window.Splide (exposed from legacy-global.js)

if (!customElements.get('tabbed-reviews-carousel')) {
  customElements.define(
    'tabbed-reviews-carousel',
    class TabbedReviewsCarousel extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
        this.carousels = new Map();
        this.autoAdvanceTimers = new Map();
        this.currentCategory = null;
        this.imageCarousel = null;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.classList.add('carousel-loaded');

        requestAnimationFrame(() => {
          this.initComponent();
        });
      }

      initComponent() {
        try {
          this.initCarousels();
          this.initImageCarousel();
          this.initTabFunctionality();
          this.setInitialCategory();
        } catch (error) {
          console.error('Error initializing tabbed reviews carousel:', error);
        }
      }

      initCarousels() {
        const Splide = window.Splide;
        const carouselElements = this.querySelectorAll('.reviews-carousel');

        carouselElements.forEach((carouselEl) => {
          const category = carouselEl.dataset.category;

          const carousel = new Splide(carouselEl, {
            type: 'loop',
            perPage: 1,
            arrows: false,
            pagination: true,
            autoplay: false,
            interval: 4000,
            classes: {
              pagination: 'splide__pagination custom-pagination',
              page: 'splide__pagination__page custom-pagination-dot'
            }
          });

          // Reset timer when user manually navigates
          carousel.on('click', () => {
            this.resetAutoAdvanceTimer(category);
          });

          carousel.on('moved', () => {
            this.resetAutoAdvanceTimer(category);
          });

          carousel.mount();
          this.carousels.set(category, carousel);

          // Apply custom pagination styling after mount
          requestAnimationFrame(() => {
            this.addCustomArrows(carouselEl, carousel);
          });
        });
      }

      initImageCarousel() {
        const Splide = window.Splide;
        const imageCarouselEl = this.querySelector('.category-image-carousel');
        if (!imageCarouselEl) return;

        this.imageCarousel = new Splide(imageCarouselEl, {
          type: 'slide',
          perPage: 1,
          arrows: false,
          pagination: false,
          drag: false,
          autoplay: false
        });

        this.imageCarousel.mount();
      }

      initTabFunctionality() {
        const tabButtons = this.querySelectorAll('.tab-button');

        tabButtons.forEach((button) => {
          button.addEventListener('click', () => {
            const targetCategory = button.dataset.tab;
            this.switchToCategory(targetCategory);
          });
        });
      }

      setInitialCategory() {
        const firstTabButton = this.querySelector('.tab-button');
        if (firstTabButton) {
          const firstCategory = firstTabButton.dataset.tab;
          this.switchToCategory(firstCategory);
        }
      }

      switchToCategory(categoryId) {
        this.stopAutoAdvance(this.currentCategory);

        this.updateTabButtons(categoryId);
        this.updateTabPanels(categoryId);
        this.updateCategoryImage(categoryId);

        this.currentCategory = categoryId;
        this.startAutoAdvance(categoryId);
      }

      updateTabButtons(activeCategory) {
        const tabButtons = this.querySelectorAll('.tab-button');
        tabButtons.forEach((button) => {
          if (button.dataset.tab === activeCategory) {
            button.classList.add('bg-white');
            button.classList.remove('bg-brand-secondary-400');
          } else {
            button.classList.remove('bg-white');
            button.classList.add('bg-brand-secondary-400');
          }
        });
      }

      updateTabPanels(activeCategory) {
        const tabPanels = this.querySelectorAll('.tab-panel');
        tabPanels.forEach((panel) => {
          if (panel.dataset.panel === activeCategory) {
            panel.classList.remove('hidden');
            panel.classList.add('block');
          } else {
            panel.classList.add('hidden');
            panel.classList.remove('block');
          }
        });
      }

      updateCategoryImage(activeCategory) {
        if (!this.imageCarousel) return;

        // Find the index of the category slide
        const categorySlides = this.querySelectorAll('.category-image-carousel .splide__slide');
        let targetIndex = 0;

        categorySlides.forEach((slide, index) => {
          if (slide.dataset.category === activeCategory) {
            targetIndex = index;
          }
        });

        // Navigate to the appropriate image slide
        this.imageCarousel.go(targetIndex);
      }

      startAutoAdvance(categoryId) {
        const carousel = this.carousels.get(categoryId);
        if (!carousel) return;

        this.stopAutoAdvance(categoryId);

        const timer = setInterval(() => {
          if (carousel && carousel.Components && carousel.Components.Controller) {
            carousel.go('+1');
          }
        }, 4000);

        this.autoAdvanceTimers.set(categoryId, timer);
      }

      resetAutoAdvanceTimer(categoryId) {
        if (categoryId === this.currentCategory) {
          this.startAutoAdvance(categoryId);
        }
      }

      stopAutoAdvance(categoryId) {
        if (!categoryId) return;

        const timer = this.autoAdvanceTimers.get(categoryId);
        if (timer) {
          clearInterval(timer);
          this.autoAdvanceTimers.delete(categoryId);
        }
      }

      addCustomArrows(carouselEl, carousel) {
        const pagination = carouselEl.querySelector('.splide__pagination');
        if (!pagination) return;

        // Create navigation container
        const navContainer = document.createElement('div');
        navContainer.className = 'flex items-center justify-center mt-4';

        // Create prev arrow (just SVG, no button wrapper)
        const prevArrow = document.createElement('div');
        prevArrow.className = 'flex items-center justify-center cursor-pointer';
        prevArrow.style.height = '16px';
        prevArrow.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.4712 3.52835C10.2109 3.268 9.78878 3.268 9.52843 3.52835L5.52843 7.52835C5.26808 7.7887 5.26808 8.21081 5.52843 8.47116L9.52843 12.4712C9.78878 12.7315 10.2109 12.7315 10.4712 12.4712C10.7316 12.2108 10.7316 11.7887 10.4712 11.5283L6.94265 7.99975L10.4712 4.47116C10.7316 4.21081 10.7316 3.7887 10.4712 3.52835Z" fill="#2B2B2B"/>
          </svg>
        `;

        // Create next arrow (just SVG, no button wrapper)
        const nextArrow = document.createElement('div');
        nextArrow.className = 'flex items-center justify-center cursor-pointer';
        nextArrow.style.height = '16px';
        nextArrow.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.52876 3.52835C5.78911 3.268 6.21122 3.268 6.47157 3.52835L10.4716 7.52835C10.7319 7.7887 10.7319 8.21081 10.4716 8.47116L6.47157 12.4712C6.21122 12.7315 5.78911 12.7315 5.52876 12.4712C5.26841 12.2108 5.26841 11.7887 5.52876 11.5283L9.05735 7.99975L5.52876 4.47116C5.26841 4.21081 5.26841 3.7887 5.52876 3.52835Z" fill="#2B2B2B"/>
          </svg>
        `;

        // Add click handlers
        prevArrow.addEventListener('click', () => {
          carousel.go('<');
          this.resetAutoAdvanceTimer(carouselEl.dataset.category);
        });

        nextArrow.addEventListener('click', () => {
          carousel.go('>');
          this.resetAutoAdvanceTimer(carouselEl.dataset.category);
        });

        // Make sure pagination is properly aligned
        pagination.style.position = 'static';
        pagination.style.bottom = 'auto';
        pagination.style.display = 'flex';
        pagination.style.alignItems = 'center';
        pagination.style.marginBottom = '6px';
        pagination.style.setProperty('padding', '0 6px', 'important');

        // Assemble navigation: prev arrow, pagination, next arrow
        navContainer.appendChild(prevArrow);
        navContainer.appendChild(pagination);
        navContainer.appendChild(nextArrow);

        // Insert navigation container after the carousel
        carouselEl.parentNode.insertBefore(navContainer, carouselEl.nextSibling);
      }

      disconnectedCallback() {
        this.autoAdvanceTimers.forEach((timer) => clearInterval(timer));
        this.autoAdvanceTimers.clear();

        this.carousels.forEach((carousel) => {
          if (carousel && carousel.destroy) {
            carousel.destroy();
          }
        });
        this.carousels.clear();
      }
    }
  );
}
